import {
  type Material,
  type MaterialMovement,
  formatAmount,
  materialLabel,
} from '@/components/sklad/materialTypes';
import { formatDateTime, parseQty } from '@/components/sklad/types';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useEffect, useState } from 'react';

const INPUT =
  'w-full text-sm text-text bg-surface border border-border rounded-lg px-3 py-2 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all';

const MOVE_LABEL: Record<MaterialMovement['type'], string> = {
  receive: 'Příjem z objednávky',
  unreceive: 'Vrácen příjem',
  issue: 'Výdej',
  adjust: 'Inventura',
};

/**
 * Pohyby jedné položky: výdej do výroby, inventura (skutečný stav) a historie.
 * `mode` určuje, která akce je otevřená jako první.
 */
export function MaterialStockModal({
  item,
  mode: initialMode,
  onSaved,
  onClose,
  onError,
}: {
  item: Material;
  mode: 'issue' | 'adjust';
  onSaved: (msg: string) => void;
  onClose: () => void;
  onError: (msg: string) => void;
}) {
  useEscapeKey(onClose);
  const [mode, setMode] = useState(initialMode);
  const [amount, setAmount] = useState('');
  const [productionRef, setProductionRef] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [movements, setMovements] = useState<MaterialMovement[] | null>(null);

  useEffect(() => {
    apiFetch<{ movements: MaterialMovement[] }>(`/api/material/items/${item.id}/movements`)
      .then((d) => setMovements(d.movements))
      .catch(() => setMovements([]));
  }, [item.id]);

  async function save() {
    const n = parseQty(amount);
    if (!Number.isFinite(n) || n < 0 || (mode === 'issue' && n === 0)) {
      onError(mode === 'issue' ? 'Zadej, kolik vydat' : 'Zadej skutečný stav');
      return;
    }
    setSaving(true);
    try {
      if (mode === 'issue') {
        await apiFetch(`/api/material/items/${item.id}/issue`, {
          method: 'POST',
          body: { amount: n, production_ref: productionRef, note },
        });
        onSaved(`Vydáno ${formatAmount(n, item.unit)}`);
      } else {
        await apiFetch(`/api/material/items/${item.id}/adjust`, {
          method: 'POST',
          body: { stock_qty: n, note },
        });
        onSaved(`Stav nastaven na ${formatAmount(n, item.unit)}`);
      }
    } catch (e: unknown) {
      onError(e instanceof ApiError ? e.describe() : 'Uložení selhalo');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <button
        type="button"
        aria-label="Zavřít"
        onClick={onClose}
        className="fixed inset-0 bg-black/30 backdrop-blur-sm cursor-default"
      />
      <div className="relative w-full max-w-lg my-10 bg-surface rounded-2xl shadow-modal p-6 reveal">
        <h2 className="text-lg font-bold text-text">{materialLabel(item)}</h2>
        <p className="text-sm text-text-secondary mt-0.5 pb-4 mb-4 border-b border-border">
          {item.category || 'Bez druhu'} · skladem{' '}
          <strong className="text-text">{formatAmount(item.stock_qty, item.unit)}</strong>
        </p>

        <div className="flex gap-1 mb-4 bg-bg rounded-lg p-1 w-fit">
          {(
            [
              ['issue', 'Výdej'],
              ['adjust', 'Inventura'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => {
                setMode(key);
                setAmount('');
              }}
              className={`text-sm font-semibold px-4 py-1.5 rounded-md cursor-pointer transition-colors ${
                mode === key ? 'bg-surface text-primary shadow-card' : 'text-text-secondary'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-sm font-medium text-text-secondary block mb-1.5">
              {mode === 'issue' ? `Kolik vydat (${item.unit})` : `Skutečný stav (${item.unit})`}
            </span>
            <input
              // biome-ignore lint/a11y/noAutofocus: okno se otevírá kvůli zadání množství
              autoFocus
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && save()}
              inputMode="decimal"
              className={INPUT}
            />
          </label>
          {mode === 'issue' && (
            <label className="block">
              <span className="text-sm font-medium text-text-secondary block mb-1.5">
                Na zakázku (nepovinné)
              </span>
              <input
                value={productionRef}
                onChange={(e) => setProductionRef(e.target.value)}
                placeholder="číslo zakázky"
                className={INPUT}
              />
            </label>
          )}
        </div>
        <label className="block mt-3">
          <span className="text-sm font-medium text-text-secondary block mb-1.5">Poznámka</span>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={mode === 'adjust' ? 'např. inventura, zmetky' : ''}
            className={INPUT}
          />
        </label>

        <div className="flex justify-end gap-3 mt-5">
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-semibold text-text-secondary border border-border rounded-lg px-4 py-2.5 hover:bg-surface-hover transition-colors cursor-pointer"
          >
            Zavřít
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-6 py-2.5 cursor-pointer transition-colors disabled:opacity-50"
          >
            {saving ? 'Ukládám…' : mode === 'issue' ? 'Vydat' : 'Nastavit stav'}
          </button>
        </div>

        <h3 className="text-sm font-semibold text-text mt-6 mb-2">Historie</h3>
        <div className="max-h-64 overflow-y-auto border border-border-light rounded-lg">
          {movements === null && <p className="text-sm text-text-muted p-3">Načítám…</p>}
          {movements?.length === 0 && <p className="text-sm text-text-muted p-3">Zatím nic.</p>}
          {movements?.map((m) => (
            <div
              key={m.id}
              className="flex items-start justify-between gap-3 px-3 py-2 border-b border-border-light last:border-0 text-sm"
            >
              <div className="min-w-0">
                <div className="text-text font-medium">
                  {MOVE_LABEL[m.type]}{' '}
                  <span className={m.delta < 0 ? 'text-danger' : 'text-success'}>
                    {m.delta > 0 ? '+' : ''}
                    {formatAmount(m.delta, item.unit)}
                  </span>
                </div>
                <div className="text-xs text-text-muted truncate">
                  {[
                    formatDateTime(m.created_at),
                    m.user_name,
                    m.production_ref && `zakázka ${m.production_ref}`,
                    m.note,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              </div>
              <span className="text-xs text-text-secondary shrink-0">
                → {formatAmount(m.result_qty, item.unit)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
